import type { HumanProportions } from '../../../content/characters/rigs/human.js';
import { faceLandmarks, headSurfaceKey, joinHeadPieces, skinDecal, type FaceLandmarks, type HeadDetail, type HeadPiece } from './human-head.js';

/**
 * What age and old wounds leave on a face, laid over the skin the head already has: wrinkles and burns are painted into the face's own vertex
 * colours and normals as the figure is assembled (`facePaint`), and scars are thin strips of skin on the face's own material (`scarParts`), so
 * they join the face's batch and draw in the same call. Everything is placed in skull space about the head's landmarks before the face warp,
 * and the warp then carries it with the skin, so marks follow every face shape and slider. Sag itself is a warp term (`faceWarp`).
 */
type Vec2 = [number, number];
const clamp = (v: number, a: number, b: number) => v < a ? a : v > b ? b : v;
const smooth = (v: number, a: number, b: number) => { const t = clamp((v - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
const mix = (a: number, b: number, t: number) => a + (b - a) * t;
const gauss = (d: number, width: number) => Math.exp(-((d / width) ** 2));
function shade(out: number[], amount: number, to: readonly number[]) { for (let c = 0; c < 3; c++) out[c] *= 1 - amount * (1 - to[c]); }
/** Signed distance from a point to the segment a→b (positive to its right), and how far along it the point lies (0 to 1). */
function segment(x: number, y: number, a: Vec2, b: Vec2): [number, number] {
  const lx = b[0] - a[0], ly = b[1] - a[1], l2 = lx * lx + ly * ly, t = clamp(((x - a[0]) * lx + (y - a[1]) * ly) / l2, 0, 1);
  const across = ((x - a[0]) * ly - (y - a[1]) * lx) / Math.sqrt(l2);
  return [Math.hypot(x - a[0] - t * lx, y - a[1] - t * ly) * Math.sign(across || 1), t];
}
/** Smooth value noise in three dimensions, about `cell` across, from 0 to 1. */
function noise(x: number, y: number, z: number, cell: number, seed = 0) {
  const hash = (i: number, j: number, k: number) => { const h = Math.sin(i * 127.1 + j * 311.7 + k * 74.7 + seed * 19.3) * 43758.5453; return h - Math.floor(h); };
  const fx = x / cell, fy = y / cell, fz = z / cell, ix = Math.floor(fx), iy = Math.floor(fy), iz = Math.floor(fz);
  const tx = fx - ix, ty = fy - iy, tz = fz - iz, sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty), sz = tz * tz * (3 - 2 * tz);
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
  const layer = (k: number) => lerp(lerp(hash(ix, iy, k), hash(ix + 1, iy, k), sx), lerp(hash(ix, iy + 1, k), hash(ix + 1, iy + 1, k), sx), sy);
  return lerp(layer(iz), layer(iz + 1), sz);
}

/**
 * How deep the wrinkles cut at a point of the face, from 0: forehead lines, frown lines, crow's feet, lines under the eyes, the fold from the
 * nose to the mouth and the lines down from its corners, the fine lines over the upper lip and rings round the neck. `sag` deepens the folds.
 */
function wrinkleDepth(L: FaceLandmarks, x: number, y: number, z: number, width: number, sag: number): number {
  const ax = Math.abs(x), { eye, mouth } = L, front = smooth(z, .03, .06);
  if (front <= 0 && y > 1.75) return 0;
  let depth = 0;
  // Three long lines across the forehead, arching up at the sides; the middle one deepest.
  const brow = eye.y + .019;
  if (y > brow + .008 && y < brow + .05) for (let i = 0; i < 3; i++) {
    const line = brow + .017 + i * .0095 + .0022 * (ax / .035) ** 2 + .0006 * Math.sin(ax * 260 + i);
    // The forehead is flat, so its mesh is sparse there: these lines are drawn broader than the rest to land on its vertices.
    depth += [.6, .85, .55][i] * gauss(y - line, width * 1.8) * (1 - smooth(ax, .03 + .003 * i, .05)) * front;
  }
  // Two frown lines rising from between the brows.
  if (ax < .012 && y > brow - .006 && y < brow + .018) {
    const t = (y - (brow - .004)) / .016;
    depth += .8 * gauss(ax - .0052 - .0016 * t, width * .8) * Math.sin(Math.PI * clamp(t, 0, 1)) * front;
  }
  // Crow's feet fanning from the outer corner of each eye.
  const cx = eye.x + eye.w * 1.08, cy = eye.y + eye.tilt * eye.w;
  if (ax > cx - .002 && ax < cx + .018 && Math.abs(y - cy) < .014) for (const angle of [.42, 0, -.4]) {
    const dx = Math.cos(angle), dy = Math.sin(angle), rx = ax - cx, ry = y - cy, along = rx * dx + ry * dy, across = rx * -dy + ry * dx;
    if (along > .0025 && along < .0135) depth += .85 * gauss(across, width * .85) * Math.sin(Math.PI * (along - .0025) / .011) ** .5;
  }
  // A line under each eye, curving with the lower lid.
  const u = ax - eye.x;
  if (u > -eye.w * .9 && u < eye.w * 1.25 && Math.abs(y - eye.y) < .02) {
    const line = eye.y - eye.down - .004 - .0018 * (1 - (u / (eye.w * 1.1)) ** 2) + eye.tilt * u;
    depth += (.7 + .4 * sag) * gauss(y - line, width) * Math.sin(Math.PI * (u + eye.w * .9) / (eye.w * 2.15));
  }
  // The fold from the nose wing to past the mouth corner, and the lines from the mouth corners down to the chin.
  if (y < L.noseBase + .006 && y > mouth.y - .028 && ax < mouth.w + .022) {
    const [fold, t] = segment(ax, y, [L.noseWidth + .001, L.noseBase], [mouth.w + .006, mouth.y - .008]);
    depth += (1 + .6 * sag) * gauss(fold, width * 1.5) * Math.sin(Math.PI * t) ** .6 * front;
    const [chin, s] = segment(ax, y, [mouth.w + .002, mouth.y - .003], [mouth.w + .006, mouth.y - .021]);
    depth += (.6 + .5 * sag) * gauss(chin, width * 1.2) * Math.sin(Math.PI * s) ** .7 * front;
    // Fine lines over the upper lip.
    if (y > mouth.y + .004 && y < mouth.y + .013) for (const at of [.0045, .0095, .0145])
      depth += .3 * gauss(ax - at, width * .6) * Math.sin(Math.PI * (y - mouth.y - .004) / .009);
  }
  // Rings round the front of the neck, under the jaw.
  if (y < 1.752 && y > 1.715 && z > -.01) for (const ring of [1.744, 1.731])
    depth += .45 * gauss(y - ring - .002 * (ax / .03) ** 2, width * 1.2) * (1 - smooth(ax, .022, .04)) * smooth(z, .0, .03);
  return depth;
}

type Region = (x: number, y: number, z: number) => number;
/** Burns: where each lies (an amount from 0 to 1 over the skin, with a ragged edge). `+x` is the figure's left. */
const BURNS: Record<string, (L: FaceLandmarks) => Region> = {
  cheek: () => (x, y, z) => blob(x, y, z, [.046, 1.814], [.022, .027], 1),
  half: () => (x, y, z) => {
    // The right side of the face from temple to jaw, the ear with it, stopping short of the nose and mouth.
    const edge = -.012 + .006 * (noise(0, y, 0, .012, 4) - .5), inside = smooth(-x, -edge - .004, -edge + .008);
    return inside * smooth(y, 1.752, 1.77) * (1 - smooth(y, 1.92, 1.945)) * smooth(z, -.06, -.03) * ragged(x, y, z, 2);
  },
  jaw: () => (x, y, z) => Math.max(blob(x, y, z, [.034, 1.768], [.032, .022], 3), blob(x, y, z, [.026, 1.735], [.03, .03], 3) * smooth(z, -.02, .02)),
  brow: L => (x, y, z) => blob(x, y, z, [-L.eye.x - .002, L.eye.y + .027], [.026, .018], 5),
};
function ragged(x: number, y: number, z: number, seed: number) { return smooth(noise(x, y, z, .007, seed) * .6 + noise(x, y, z, .016, seed + 1) * .7, .35, .6); }
function blob(x: number, y: number, z: number, [cx, cy]: Vec2, [rx, ry]: Vec2, seed: number) {
  const r = Math.hypot((x - cx) / rx, (y - cy) / ry) + .35 * (noise(x, y, z, .009, seed) - .5);
  return (1 - smooth(r, .7, 1.05)) * smooth(z, -.02, .02);
}

/**
 * Paints wrinkles and a burn into the face skin's colours and normals as it is placed, or nothing when the face has neither. The point is in skull
 * space before the warp; `color` and `normal` are changed in place.
 */
export function facePaint(p: HumanProportions, o: { wrinkles: number; sag: number; burn: string; detail: HeadDetail }): ((x: number, y: number, z: number, color: number[], normal: number[]) => void) | undefined {
  const burn = BURNS[o.burn];
  if (o.wrinkles <= 0 && !burn) return undefined;
  const L = faceLandmarks(p), width = .0019, strength = o.wrinkles * .7;
  const region = burn?.(L), e = .00025;
  return (x, y, z, color, normal) => {
    if (strength > 0) {
      const depth = wrinkleDepth(L, x, y, z, width, o.sag);
      if (depth > .002) {
        shade(color, clamp(.62 * strength * depth, 0, .7), [.5, .37, .34]);
        // A crease turns the skin on either side of it toward its bottom: the normal leans the way the depth grows.
        const gx = (wrinkleDepth(L, x + e, y, z, width, o.sag) - wrinkleDepth(L, x - e, y, z, width, o.sag)) / (2 * e);
        const gy = (wrinkleDepth(L, x, y + e, z, width, o.sag) - wrinkleDepth(L, x, y - e, z, width, o.sag)) / (2 * e);
        const tilt = .000175 * strength;
        normal[0] += gx * tilt; normal[1] += gy * tilt;
        const l = Math.hypot(normal[0], normal[1], normal[2]) || 1; normal[0] /= l; normal[1] /= l; normal[2] /= l;
      }
    }
    if (region) {
      const amount = region(x, y, z);
      if (amount > .01) {
        // Healed burns mottle: raw red, pale shiny grafts and darker puckered patches, ringed by a darker margin.
        const m = noise(x, y, z, .0055, 9) * .65 + noise(x, y, z, .002, 11) * .35, rim = amount * (1 - amount) * 4;
        shade(color, .7 * amount, m > .58 ? [1.16, .97, .93] : m < .36 ? [.74, .53, .5] : [1.06, .8, .74]);
        shade(color, .25 * rim, [.78, .55, .5]);
        const gx = (noise(x + e, y, z, .0055, 9) - noise(x - e, y, z, .0055, 9)) / (2 * e), gy = (noise(x, y + e, z, .0055, 9) - noise(x, y - e, z, .0055, 9)) / (2 * e);
        const tilt = .00064 * amount;
        normal[0] -= gx * tilt; normal[1] -= gy * tilt;
        const l = Math.hypot(normal[0], normal[1], normal[2]) || 1; normal[0] /= l; normal[1] /= l; normal[2] /= l;
      }
    }
  };
}

/** A healed cut: points of its line in the front view (it bends through them), how wide it is and how far it stands proud (raised over hair it crosses). */
interface Cut { points: Vec2[]; width: number; over?: boolean }
const SCARS: Record<string, (L: FaceLandmarks) => Cut[]> = {
  cheek: ({ eye, mouth }) => [{ points: [[eye.x + .007, eye.y - .015], [eye.x + .014, eye.y - .03], [mouth.w + .017, mouth.y + .005]], width: .0032 }],
  eye: ({ eye }) => [
    { points: [[-eye.x + .001, eye.y + .027], [-eye.x - .0005, eye.y + .013], [-eye.x - .001, eye.y + eye.up + .0032]], width: .0028, over: true },
    { points: [[-eye.x + .001, eye.y - eye.down - .0032], [-eye.x + .003, eye.y - .019], [-eye.x + .006, eye.y - .032]], width: .0028 },
  ],
  brow: ({ eye }) => [{ points: [[eye.x + .006, eye.y + .029], [eye.x + .004, eye.y + .02], [eye.x + .0025, eye.y + .012]], width: .0024, over: true }],
  lip: ({ mouth, noseBase }) => [{ points: [[-.0085, noseBase - .0025], [-.0062, mouth.y + .002], [-.0042, mouth.y - .012]], width: .0022, over: true }],
  jaw: ({ mouth }) => [{ points: [[-.016, mouth.y - .032], [-.033, mouth.y - .026], [-.05, mouth.y - .011]], width: .003 }],
  chin: ({ mouth }) => [{ points: [[.0035, mouth.y - .021], [.008, mouth.y - .026], [.0125, mouth.y - .0275]], width: .0024 }],
  forehead: ({ eye }) => [{ points: [[-.024, eye.y + .07], [-.006, eye.y + .058], [.014, eye.y + .046]], width: .0034 }],
  nose: ({ noseBase }) => [{ points: [[-.0075, mix(noseBase, 1.866, .62) + .001], [0, mix(noseBase, 1.866, .62) - .0008], [.0075, mix(noseBase, 1.866, .62) + .0012]], width: .0022 }],
  veteran: ({ eye, mouth }) => [
    { points: [[-eye.x - .012, eye.y - .02], [-eye.x - .006, eye.y - .031]], width: .0024 },
    { points: [[eye.x + .006, eye.y + .027], [eye.x + .004, eye.y + .016]], width: .0022, over: true },
    { points: [[.006, mouth.y - .021], [.0115, mouth.y - .026]], width: .0022 },
  ],
};
const scarCache = new Map<string, HeadPiece[]>();
/** A point along a line bent through `points` (a quadratic Bézier for three points, straight for two), with its direction. */
function along(points: Vec2[], t: number): [Vec2, Vec2] {
  if (points.length === 2) { const [a, b] = points; return [[mix(a[0], b[0], t), mix(a[1], b[1], t)], [b[0] - a[0], b[1] - a[1]]]; }
  const [a, b, c] = points, s = 1 - t;
  return [[s * s * a[0] + 2 * s * t * b[0] + t * t * c[0], s * s * a[1] + 2 * s * t * b[1] + t * t * c[1]],
    [2 * s * (b[0] - a[0]) + 2 * t * (c[0] - b[0]), 2 * s * (b[1] - a[1]) + 2 * t * (c[1] - b[1])]];
}
/**
 * The chosen scar as strips of the face's own skin: pale, slightly pink and raised along the middle, drawn in at its edges, tapering at its ends.
 * A scar crossing an eyebrow stands over it, so the brow parts where the cut ran. Figures in the world lift it further off the skin, since
 * their coarser skin cuts chords that would otherwise cover it.
 */
export function scarParts(p: HumanProportions, id: string, detail: HeadDetail): HeadPiece[] {
  const design = SCARS[id];
  if (!design) return [];
  const key = `${headSurfaceKey(p)}:scar:${id}`, known = scarCache.get(key);
  if (known) return known;
  const L = faceLandmarks(p), base = .0011;
  const strips = design(L).map(cut => skinDecal(p, (u, v) => {
    const [[x, y], [dx, dy]] = along(cut.points, u), l = Math.hypot(dx, dy) || 1, half = cut.width / 2 * Math.max(.18, Math.sin(Math.PI * u) ** .45);
    return [x + -dy / l * (v - .5) * 2 * half, y + dx / l * (v - .5) * 2 * half];
  }, 12, 2, base + (cut.over ? .0005 : 0), (u, v) => {
    // Across the scar: skin at the edges, drawn darker just inside them, pale and pinkish down the middle.
    const middle = gauss(v - .5, .2), edge = gauss(Math.abs(v - .5) - .3, .1), end = Math.sin(Math.PI * u) ** .3;
    return [mix(1, mix(1, .9, edge) * mix(1, 1.16, middle), end), mix(1, mix(1, .82, edge) * mix(1, 1.02, middle), end), mix(1, mix(1, .8, edge) * mix(1, 1, middle), end)];
  }, (u, v) => .5 + .5 * Math.sin(Math.PI * v) * Math.sin(Math.PI * u) ** .5));
  const result = strips.some(strip => strip) ? [joinHeadPieces('face', strips)] : [];
  if (scarCache.size >= 64) scarCache.delete(scarCache.keys().next().value!);
  scarCache.set(key, result);
  return result;
}
